import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { ReplaySubject, Observable } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';

import { CurrentUser } from 'modules/shared/models/account.models';
import { MFA_CODE_PATTERN } from 'modules/shared/constants/mfa-code-pattern.constants';
import { SnackbarService } from 'app/services/snackbar.service';

import { AppState, Selectors } from 'store/root-reducer';
import * as AuthActions from 'store/actions/auth.actions';

declare let QRCode: any;

@Component({
  selector: 'artemis-setup-multi-factor-page',
  templateUrl: './setup-multi-factor.page.html',
  styleUrls: ['./setup-multi-factor.page.scss']
})
export class SetupMultiFactorPage implements OnInit, OnDestroy {

  private unsubscribe$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private mfaCodePattern: string = MFA_CODE_PATTERN;
  private isGetEnableMfa: boolean;

  mfaSetupForm: FormGroup;

  loading$: Observable<boolean>;
  currentUser: CurrentUser;
  qrcodeImg: string;
  secretCode: string = '';

  constructor(private store: Store<AppState>,
              private router: Router,
              private snackbar: SnackbarService,
              private formBuilder: FormBuilder) { }

  ngOnInit() {
    this.buildForms();
    this.subscribeMfaSetup();
    this.initLoading();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    this.store.dispatch(new AuthActions.ClearEnableMfa());
    this.store.dispatch(new AuthActions.ClearMfaConfirm());
  }

  /**
   * Inits observables for loading spinner
   * @method initLoading
   */
  private initLoading(): void {
    this.loading$ = this.store.select(Selectors.auth.getLoading)
      .pipe(
        takeUntil(this.unsubscribe$),
        map((loadingActionsList: Array<AuthActions.AuthActionTypes>) => {
          return loadingActionsList.includes(AuthActions.AuthActionTypes.ENABLE_MFA ||
                                             AuthActions.AuthActionTypes.MFA_CONFIRM ||
                                             AuthActions.AuthActionTypes.GET_USER);
        })
      );
  }

  /**
   * Build form for mfa setup page
   * @method buildForms
   */
  private buildForms(): void {
    this.mfaSetupForm = this.formBuilder.group({
      code: new FormControl('', [Validators.required, Validators.pattern(this.mfaCodePattern)])
    });
  }

  /**
   * Subscribe mfa setup page actions
   * @method subscribeMfaSetup
   */
  private subscribeMfaSetup(): void {
    // Subscribe get current user
    this.store.select(Selectors.auth.getUser)
      .pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe((user: CurrentUser) => {
        if (user) {
          this.currentUser = user;
          if (this.currentUser.preferredMFA && this.currentUser.preferredMFA !== 'NOMFA') {
            this.router.navigateByUrl('select-domain');
          }
          if (!this.isGetEnableMfa) {
            this.store.dispatch(new AuthActions.EnableMfa());
            this.isGetEnableMfa = true;
          }
        }
      });

    // Subscribe enable mfa
    this.store.select(Selectors.auth.enableMfa)
      .pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe((secretCode: string) => {
        if (secretCode) {
          this.secretCode = secretCode;
          QRCode.toDataURL(`otpauth://totp/${this.currentUser.email}?secret=${secretCode}&issuer=Artemis`)
            .then((url: string) => {
              this.qrcodeImg = url;
            });
        }
      });

    // Subscribe mfa confirm
    this.store.select(Selectors.auth.mfaConfirm)
      .pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe((response: boolean) => {
        if (response) {
          this.qrcodeImg = null;
          this.router.navigateByUrl('select-domain');
        }
      });
  }

  /**
   * Confirm enable mfa
   * @method mfaConfirm
   */
  mfaConfirm(): void {
    if (this.mfaSetupForm.invalid) {
      return;
    }

    this.store.dispatch(new AuthActions.MfaConfirm({ code: this.mfaSetupForm.value.code }));
  }

  /**
   * Returns flag show materror or no
   * @method showFormControlErrors
   */
  showFormControlErrors(form: FormGroup, formControlName: string): boolean {
    return form.get(formControlName).invalid && (form.get(formControlName).dirty || form.get(formControlName).touched);
  }

  /**
   * Logout and back to login page
   * @method goBack
   */
  goBack(): void {
    this.store.dispatch(new AuthActions.LogOut({ isSessionTimeOut: false }));
  }

  /**
   * Copy secret code into clipboard
   * @method copySecretCode
   */
  copySecretCode() {
    (navigator as any).clipboard.writeText(this.secretCode)
      .then(() => {
        this.snackbar.success('Copied');
      });
  }
}
